// Metered agent cost as money (11 §Money: integer micros, 1 micro = 0.000001 of the currency).
// HOME-DASH (founder review 2026-10-27: almost no run costs a cent, so "$0.00" read as free; the
// same rule as wp/WF-POLISH's Workforce formatter, here for every app): a whole unit or more keeps
// the currency's decimals ("$12.40"); less keeps two significant digits ("$0.00052", "$0.05");
// under one micro reads "< $0.000001".

const MICRO = 0.000001;

function currencyFormat(currency: string, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  return new Intl.NumberFormat("en-US", { style: "currency", currency, ...opts });
}

/** The currency's own decimals (JPY 0, USD 2), or 2 for an unknown code. */
function currencyDigits(currency: string): number {
  try {
    return currencyFormat(currency, {}).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

function format(major: number, currency: string | undefined, min: number, max: number): string {
  if (currency) {
    try {
      return currencyFormat(currency, {
        minimumFractionDigits: min,
        maximumFractionDigits: max,
      }).format(major);
    } catch {
      // An unknown currency code falls through to the plain number.
    }
  }
  return major.toLocaleString("en-US", { minimumFractionDigits: min, maximumFractionDigits: max });
}

/** Micros of a currency unit as money: "$12.40", "$0.00052", "< $0.000001", "$0.00". */
export function formatMicroMoney(micros: number, currency: string | undefined): string {
  const major = micros / 1_000_000;
  const abs = Math.abs(major);
  const whole = currency ? Math.min(2, currencyDigits(currency)) : 2;
  if (abs === 0 || abs >= 1) {
    const digits = currency && abs >= 1 ? currencyDigits(currency) : whole;
    return format(major, currency, digits, digits);
  }
  if (abs < MICRO)
    return `${major < 0 ? "> " : "< "}${format(major < 0 ? -MICRO : MICRO, currency, 6, 6)}`;
  // Two significant digits: 0.05 → 2 decimals, 0.00052 → 5, 0.0000031 → 7.
  const digits = Math.max(whole, Math.ceil(-Math.log10(abs)) + 1);
  return format(major, currency, whole, digits);
}

/** "8,120", "12k", "3.4M". */
export function formatTokenCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  return n >= 10_000 ? `${Math.round(n / 1000)}k` : n.toLocaleString("en-US");
}
